import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tag, Tooltip, Typography } from 'antd';
import TableIndex from '@shared/table/TableIndex';
import type { TableService } from '@shared/table/TableIndex';
import FullDayRangePicker from '@components/shared/FullDayRangePicker';
import { MultipleValue, SearchValue } from '@shared/table/Rules/component';

const { Text } = Typography;

const ACTION_COLORS: Record<string, string> = {
  'ai.conversation.created':  'blue',
  'ai.conversation.archived': 'default',
  'ai.run.cancelled':         'orange',
  'ai.api.operator_called':   'purple',
  'ai.api.admin_called':      'geekblue',
};

const ALL_ACTIONS = Object.keys(ACTION_COLORS);

interface AiAuditLogMetadata {
  controller?: string;
  action?: string;
  method?: string;
  path?: string;
  conversation_id?: string;
  run_id?: string;
  [key: string]: unknown;
}

interface AiAuditLogRow {
  id: string;
  created_at?: string;
  action?: string;
  metadata?: AiAuditLogMetadata;
  keyword?: Record<string, unknown> | null;
  ip_address?: string | null;
  [key: string]: unknown;
}

interface AiAuditLogListProps {
  service: TableService;
}

type TFn = (key: string, options?: Record<string, unknown>) => string;

const describeApiCall = (controller: string | undefined, action: string | undefined, t: TFn): string | null => {
  if (!controller || !action) return null;
  const resource = controller.split('/').pop();
  const resourceLabel = t(`models.ai_audit_log.resources.${resource}`, { defaultValue: resource });
  const verbLabel = t(`models.ai_audit_log.action_verbs.${action}`, { defaultValue: action });
  return `${verbLabel}${resourceLabel}`;
};

// Flatten keyword JSON into readable "key: value" pairs, skipping nested objects
const formatKeyword = (keyword: Record<string, unknown> | null | undefined, t: TFn): Array<{ key: string; value: string }> => {
  if (!keyword || typeof keyword !== 'object') return [];
  return Object.entries(keyword).map(([k, v]) => {
    const display = typeof v === 'object' ? JSON.stringify(v) : String(v);
    const label = t(`models.ai_audit_log.keyword_fields.${k}`, { defaultValue: k });
    return { key: label, value: display };
  });
};

const AiAuditLogList: React.FC<AiAuditLogListProps> = ({ service }) => {
  const { t } = useTranslation();
  const [lastModified] = useState(0);
  const tFn: TFn = t as unknown as TFn;

  const columns = useMemo<unknown[]>(() => [
    {
      name: t('common.created_at'),
      selector: (row: AiAuditLogRow) => row.created_at,
      sortable: true,
      sortFieldName: 'created_at',
      width: '180px',
    },
    {
      name: t('models.ai_audit_log.operation'),
      selector: (row: AiAuditLogRow) => row.action,
      width: '160px',
      cell: (row: AiAuditLogRow) => {
        const { action, metadata = {} as AiAuditLogMetadata } = row;
        const description = describeApiCall(metadata.controller, metadata.action, tFn);

        if (description) {
          return (
            <Tooltip title={`${metadata.method || ''} ${metadata.path || ''}`}>
              <Text style={{ fontSize: 13 }}>{description}</Text>
            </Tooltip>
          );
        }

        const id = metadata.conversation_id || metadata.run_id;
        return (
          <Tooltip title={id}>
            <Tag color={(action && ACTION_COLORS[action]) || 'default'}>
              {t(`models.ai_audit_log.actions.${action}`, { defaultValue: action })}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      name: t('models.ai_audit_log.keyword'),
      selector: (row: AiAuditLogRow) => row.keyword,
      cell: (row: AiAuditLogRow) => {
        const pairs = formatKeyword(row.keyword, tFn);
        if (!pairs.length) return <Text type="secondary">—</Text>;
        return (
          <Tooltip title={JSON.stringify(row.keyword, null, 2)}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {pairs.map(({ key, value }) => (
                <span key={key} style={{ fontSize: 12 }}>
                  <Text type="secondary">{key}:</Text>{' '}
                  <Text>{value}</Text>
                </span>
              ))}
            </div>
          </Tooltip>
        );
      },
    },
    {
      name: t('models.ai_audit_log.ip_address'),
      selector: (row: AiAuditLogRow) => row.ip_address,
      width: '130px',
      cell: (row: AiAuditLogRow) => (
        <Text type="secondary" style={{ fontSize: 12 }}>{row.ip_address || '—'}</Text>
      ),
    },
  ], [t]);

  const supportRules = useMemo(() => [
    {
      label: t('common.created_at'),
      component: FullDayRangePicker,
      inputName: 'date_from_date_to',
      fromKey: 'date_from',
      toKey: 'date_to',
      defaultPinned: true,
    },
    {
      label: t('models.ai_audit_log.keyword'),
      component: SearchValue,
      inputName: 'keyword',
    },
    {
      label: t('models.ai_audit_log.action'),
      component: MultipleValue,
      inputName: 'action_type',
      defaultOptions: ALL_ACTIONS.map(a => ({
        label: t(`models.ai_audit_log.actions.${a}`, { defaultValue: a }),
        value: a,
      })),
    },
  ], [t]);

  return (
    <TableIndex
      columns={columns}
      searchable={false}
      tableId="operatorAiAuditLogsTable"
      defaultQuery={{
        sorting_field: 'created_at',
        sorting_direction: 'desc',
      }}
      supportRules={supportRules}
      lastModified={lastModified}
      service={service}
    />
  );
};

export default AiAuditLogList;
